/**
 * Two charges that are really one purchase — §7.4's arithmetic meeting the
 * way restaurants and taxis actually bill.
 *
 * `findSamePurchase` (in `receiptExtraction`) catches this at the moment a
 * second photo is filed, which is the right place and came too late for
 * everything already in the ledger. Once two charges exist there is nothing
 * to join them: attaching the same receipt to both is *permitted*, because a
 * hotel bill legitimately sits behind several charges, so the app watches you
 * connect them and merges nothing. Matt hit exactly that on 2026-11-03 with
 * an Uber fare and the same fare plus its tip — $85.65 taken off the budget
 * for a $46.72 ride.
 *
 * Two signals, either of which is enough:
 *
 * - **A shared receipt.** One document filed against two charges of the same
 *   vendor on the same day is the strongest evidence there is: the person
 *   was trying to connect them.
 * - **A tip-shaped difference.** Same vendor, same day, the larger amount no
 *   more than `maxUpliftFraction` above the smaller. A tip is added, never
 *   subtracted.
 *
 * Deliberately *not* a signal on its own: two charges of the same amount on
 * the same day. Two identical taxi fares on one day is an ordinary Tuesday,
 * and the existing re-capture check already covers a receipt filed twice.
 *
 * This only ever *proposes*. Nothing merges without someone saying so, and a
 * charge on a submitted packet must be refused by the caller — the business
 * office is holding a document that names it.
 */

export interface MergeableCharge {
  id?: string | undefined
  vendor: string
  amount: number
  /** ISO timestamp of the purchase. */
  transactionDate: string
  receiptIds: readonly string[]
  status: string
  budgetCodeId: string
}

export interface DuplicateChargePair<T extends MergeableCharge> {
  /** The one to keep: the larger amount, which is what the card was charged. */
  keep: T
  /** The one to fold into it. */
  remove: T
  reason: 'shared-receipt' | 'tip-sized-difference'
}

function sameDay(a: string, b: string): boolean {
  return a.slice(0, 10) === b.slice(0, 10)
}

function sameVendor(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase()
}

/**
 * Pairs of charges that look like one purchase, newest first.
 *
 * A charge appears in at most one pair, so a run of three near-identical
 * charges proposes one merge at a time rather than a tangle the reader has to
 * resolve in their head.
 */
export function findDuplicateCharges<T extends MergeableCharge>(
  charges: readonly T[],
  { maxUpliftFraction = 0.6 } = {},
): DuplicateChargePair<T>[] {
  // A voided charge is already out of every total; pairing it would propose
  // undoing a correction someone made deliberately.
  const live = charges.filter((charge) => charge.status !== 'void')
  const paired = new Set<T>()
  const pairs: DuplicateChargePair<T>[] = []

  for (let i = 0; i < live.length; i += 1) {
    const a = live[i]!
    if (paired.has(a)) continue

    for (let j = i + 1; j < live.length; j += 1) {
      const b = live[j]!
      if (paired.has(b)) continue
      if (!sameVendor(a.vendor, b.vendor)) continue
      if (!sameDay(a.transactionDate, b.transactionDate)) continue
      // Different codes mean somebody split this on purpose — a shared meal
      // charged partly to one budget and partly to another.
      // Compared as document ids, not through `normalizeAccountCode` — that
      // strips everything but digits, so two different codes both reduced to
      // the empty string and every split pair looked like a duplicate. The
      // test for a deliberate split is what caught it.
      if (a.budgetCodeId !== b.budgetCodeId) continue

      const shared = a.receiptIds.some((id) => b.receiptIds.includes(id))
      const lower = Math.min(a.amount, b.amount)
      const higher = Math.max(a.amount, b.amount)
      const tipShaped =
        lower > 0 && higher > lower && (higher - lower) / lower <= maxUpliftFraction

      // Each side holding its own document answers the guess: two Amazon
      // orders on one day, $42 and $58, are 38% apart and would otherwise be
      // offered as one purchase — and accepting deletes a real charge. A
      // restaurant's two slips are one bill photographed twice, so the pair
      // this rule exists for has at most one document attached when it is
      // found. A *shared* document still pairs them whatever they carry: that
      // is somebody connecting the two on purpose.
      const documentedSeparately =
        !shared && a.receiptIds.length > 0 && b.receiptIds.length > 0

      if (!shared && (!tipShaped || documentedSeparately)) continue

      const keep = a.amount >= b.amount ? a : b
      const remove = keep === a ? b : a
      pairs.push({ keep, remove, reason: shared ? 'shared-receipt' : 'tip-sized-difference' })
      paired.add(a)
      paired.add(b)
      break
    }
  }

  return pairs.sort((x, y) => y.keep.transactionDate.localeCompare(x.keep.transactionDate))
}

/**
 * Every document from both charges, in order, without repeats.
 *
 * Kept as a pure function with its own test because this one line decides
 * whether an itemised slip survives a merge, and inlining it into the
 * Firestore transaction took it out of reach of the domain suite.
 */
export function mergedReceiptIds(
  keepIds: readonly string[],
  removeIds: readonly string[],
): string[] {
  return [...new Set([...keepIds, ...removeIds])]
}
